import { useMemo } from 'react';

import { type Props as PermissionsProps, usePermissions } from './use-permissions.hook';

export const createUserPermissions = (permissions: ReturnType<typeof usePermissions>) => {
	const { hasAnyPermission, usePdf, useExport, useImport, useBlockContacts, useCustomRoles } = permissions;

	const canAdmin = () => hasAnyPermission('CUSTOMERS_ADMIN');

	// Login and seat administration (invite, sync, operator seat) is a users right, not a contacts right.
	const canAdminUsers = () => hasAnyPermission('USERS_ADMIN');

	const canBlock = () => !!useBlockContacts && hasAnyPermission('CUSTOMERS_BLOCK_ADMIN');

	// Administering a login (reset password, (de)activate). `USERS_OWN_PROFILE_ADMIN` only covers
	// the signed-in user's own account, so it only counts when `ownUser` is true.
	const canUpdateUserAccess = (ownUser?: boolean) =>
		hasAnyPermission('USERS_PROFILE_ADMIN', 'USERS_ADMIN') ||
		(ownUser === true && hasAnyPermission('USERS_OWN_PROFILE_ADMIN'));

	const userPermissions = {
		canRead: () => hasAnyPermission('CUSTOMERS_READER'),
		canCreate: canAdmin,
		canUpdate: canAdmin,
		canDelete: canAdmin,
		canArchive: canAdmin,
		canAddAttachment: (ownUser?: boolean) =>
			hasAnyPermission('CUSTOMERS_ATTACHMENTS_WRITER') ||
			(ownUser && hasAnyPermission('CUSTOMERS_ATTACHMENTS_OWN_WRITER')),
		canRemoveAttachment: (ownAttachment?: boolean, ownUser?: boolean) =>
			hasAnyPermission('CUSTOMERS_ATTACHMENTS_DELETER') ||
			(ownAttachment && hasAnyPermission('CUSTOMERS_ATTACHMENTS_OWN_DELETER')) ||
			(ownAttachment && ownUser && hasAnyPermission('CUSTOMERS_OWN_ATTACHMENTS_OWN_DELETER')),
		canAddComment: () => hasAnyPermission('CUSTOMERS_COMMENTS_OWN_WRITER'),
		canEditComment: (ownComment?: boolean, ownUser?: boolean) =>
			(ownComment && hasAnyPermission('CUSTOMERS_COMMENTS_OWN_WRITER')) ||
			(ownComment && ownUser && hasAnyPermission('CUSTOMERS_OWN_COMMENTS_OWN_WRITER')),
		canRemoveComment: (ownComment?: boolean, ownUser?: boolean) =>
			hasAnyPermission('CUSTOMERS_COMMENTS_DELETER') ||
			(ownComment && hasAnyPermission('CUSTOMERS_COMMENTS_OWN_DELETER')) ||
			(ownComment && ownUser && hasAnyPermission('CUSTOMERS_OWN_COMMENTS_OWN_DELETER')),
		canSeeUsers: () => userPermissions.canRead(),
		canEditUsers: canAdmin,
		canInviteUsers: canAdminUsers,
		canChangeRole: () => !!useCustomRoles && canAdminUsers(),
		canEditUserGroups: () => hasAnyPermission('ACCOUNT_SETTINGS_ADMIN'),
		canArchiveUsers: canAdmin,
		canUndoArchiveUsers: canAdmin,
		canDeleteUsers: canAdmin,
		canBlockUsers: canBlock,
		canUpdateUserAccess,
		canUnblockUsers: canBlock,
		// Matches the legacy handler: the own generator right counts on every row, not only the own one.
		canGenerateDocument: (_ownUser?: boolean) => {
			if (!usePdf) return false;
			return hasAnyPermission('CUSTOMERS_DOCUMENT_GENERATOR', 'CUSTOMERS_OWN_DOCUMENT_GENERATOR');
		},
		canChangeKind: () => hasAnyPermission('CUSTOMERS_MAINTENANCE_ADMIN'),
		canClearSync: canAdminUsers,
		canChangeOperator: canAdminUsers,
		canExportUsers: () => {
			if (!useExport) return false;
			return hasAnyPermission('CUSTOMERS_EXPORTER');
		},
		canImportUsers: () => {
			if (!useImport) return false;
			return hasAnyPermission('CUSTOMERS_IMPORTER');
		},
		canCreateUsers: canAdmin,
		canChangeCover: canAdmin,
	};

	return userPermissions;
};

export const useUserPermissions = (props: PermissionsProps) => {
	const permissions = usePermissions(props);

	return useMemo(() => createUserPermissions(permissions), [permissions]);
};
